import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { LayoutDashboard, Network, Shield } from "lucide-react";
import { CustomDropdown } from "../../components/CustomDropdown ";
import { hasPermission } from "../../utils/permissionsHelper";
import SingleSelectApiDropdown from "../../components/SingleSelectApiDropdown";
import Dashboard from "./Dashboard";
import SwitchDashboard from "./SwitchDashboard";
import FirewallDashboard from "./FirewallDashboard";
import CameraDashboard from "./CameraDashboard";
import CustomDashboardView from "../CustomDashboard/CustomDashboardView";
import CustomDashboardV2 from "../CustomDashboardV2/CustomDashboardV2";
import { useClientLevel } from "../../hooks/useClientLevel";
import baseapi from "../../services/baseapi";

const CLIENT_TAB_OPTIONS = [
    { label: "Overview", value: "overview" },
    { label: "Switch", value: "switch" },
    { label: "Firewall", value: "firewall" },
    { label: "Camera", value: "camera" },
];

const GLOBAL_TAB_OPTIONS = [
    { label: "Overview", value: "overview" },
];

const DashboardMain = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const currentLevel = useClientLevel();
    const isGlobal = currentLevel.name === "global";

    const activeTab = searchParams.get("tab") || "overview";

    // State for individual dashboard filters
    const [selectedSwitch, setSelectedSwitch] = useState<any>(null);
    const [selectedFirewall, setSelectedFirewall] = useState<any>(null);

    // Dynamic list of custom dashboards loaded from API
    const [customOptions, setCustomOptions] = useState<any[]>(
        isGlobal ? GLOBAL_TAB_OPTIONS : CLIENT_TAB_OPTIONS
    );

    useEffect(() => {
        const fetchCustom = async () => {
            const baseOptions = isGlobal ? GLOBAL_TAB_OPTIONS : CLIENT_TAB_OPTIONS;
            try {
                const params: any = {};
                if (!isGlobal && currentLevel.id) {
                    params.query = JSON.stringify({ client_id: currentLevel.id });
                }

                // Fetch V1
                // let v1Formatted: any[] = [];
                // try {
                //     const resV1 = await baseapi.get("/analytics/dashboards", { params });
                //     const listV1 = resV1.data?.data || [];
                //     v1Formatted = listV1.map((db: any) => ({
                //         label: db.name,
                //         value: String(db.id),
                //     }));
                // } catch (e) {
                //     console.error("V1 dashboards load error", e);
                // }

                // Fetch V2
                let v2Formatted: any[] = [];
                if (!isGlobal && hasPermission("cdash", "read")) {
                    try {
                        const resV2 = await baseapi.get("/cdash/dashboards", { params });
                        let listV2 = resV2.data;
                        if (listV2 && listV2.status !== undefined && Array.isArray(listV2.data)) {
                            listV2 = listV2.data;
                        }
                        if (Array.isArray(listV2)) {
                            v2Formatted = listV2.map((db: any) => ({
                                label: db.name,
                                value: `v2-${db.id}`,
                            }));
                        }
                    } catch (e) {
                        console.error("V2 dashboards load error", e);
                    }
                }

                setCustomOptions([...baseOptions, ...v2Formatted]);
            } catch (err) {
                console.error("Failed to load custom dashboards:", err);
                setCustomOptions(baseOptions);
            }
        };

        fetchCustom();
    }, [currentLevel.id, isGlobal]);

    const handleTabChange = (value: string) => {
        setSearchParams({ tab: value });
        // Reset selections when switching tabs
        setSelectedSwitch(null);
        setSelectedFirewall(null);
    };

    return (
        <div className="h-screen bg-gray-900 text-gray-100 font-sans overflow-hidden flex flex-col pb-20">
            {/* Header Row: Heading + Dropdowns */}
            <div className="flex-none flex items-center justify-between px-3 pt-1 pb-1">
                <h1 className="text-base font-bold text-white flex items-center gap-2 tracking-tight text-lg">
                    <div className="p-1.5 rounded bg-orange-600">
                        <LayoutDashboard className="w-4 h-4 text-white" />
                    </div>
                    Dashboard
                </h1>
                <div className="flex items-center gap-2">

                    {/* Switch filter dropdown — only on switch tab, non-global */}
                    {!isGlobal && activeTab === "switch" && (
                        <div className="w-56">
                            <SingleSelectApiDropdown
                                apiEndpoint="/assets"
                                displayProperty="name"
                                secondaryDisplayProperty="ip_address"
                                searchFields={["name", "ip_address"]}
                                searchInputPlaceholder="Search switches..."
                                placeholder="All Switches"
                                value={selectedSwitch}
                                onChange={(item) => setSelectedSwitch(item)}
                                additionalQueries={{
                                    asset_type: "switch",
                                    ...(currentLevel.id ? { client_id: currentLevel.id } : {}),
                                }}
                                idProperty="id"
                                showSecondaryDisplayProperty={false}
                                icon={Network}
                                height="h-8"
                                buttonClassName="py-1 pl-2 pr-7 text-xs"
                            />
                        </div>
                    )}

                    {/* Firewall filter dropdown — only on firewall tab, non-global */}
                    {!isGlobal && activeTab === "firewall" && (
                        <div className="w-56">
                            <SingleSelectApiDropdown
                                apiEndpoint="/assets"
                                displayProperty="name"
                                secondaryDisplayProperty="ip_address"
                                searchFields={["name", "ip_address"]}
                                searchInputPlaceholder="Search firewalls..."
                                placeholder="All Firewalls"
                                value={selectedFirewall}
                                onChange={(item) => setSelectedFirewall(item)}
                                additionalQueries={{
                                    asset_type: "firewall", // Adjust if your DB uses a different key for firewalls
                                    ...(currentLevel.id ? { client_id: currentLevel.id } : {}),
                                }}
                                idProperty="id"
                                showSecondaryDisplayProperty={false}
                                icon={Shield}
                                height="h-8"
                                buttonClassName="py-1 pl-2 pr-7 text-xs"
                            />
                        </div>
                    )}

                    {/* Tab dropdown */}
                    <CustomDropdown
                        data={customOptions}
                        selectedValue={activeTab}
                        onSelect={handleTabChange}
                        placeholder="Select Dashboard"
                        width="w-48"
                        buttonHeight={8}
                    />
                </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 min-h-0 overflow-hidden">
                {activeTab === "overview" && <Dashboard />}
                {activeTab === "switch" && !isGlobal && (
                    <SwitchDashboard selectedSwitch={selectedSwitch} />
                )}
                {activeTab === "firewall" && !isGlobal && (
                    <FirewallDashboard selectedFirewall={selectedFirewall} />
                )}
                {activeTab === "camera" && !isGlobal && (
                    <CameraDashboard />
                )}

                {/* Fallbacks if Global is somehow on specific tabs */}
                {(activeTab === "switch" || activeTab === "firewall" || activeTab === "camera") && isGlobal && <Dashboard />}

                {/* Custom Dashboard View V1 */}
                {!["overview", "switch", "firewall", "camera"].includes(activeTab) && !activeTab.startsWith("v2-") && (
                    <CustomDashboardView dashboardId={Number(activeTab)} clientId={currentLevel.id} />
                )}

                {/* Custom Dashboard View V2 */}
                {activeTab.startsWith("v2-") && (
                    <CustomDashboardV2 dashboardId={Number(activeTab.replace("v2-", ""))} readOnly={true} />
                )}
            </div>
        </div>
    );
};

export default DashboardMain;